import React from 'react';
import PropTypes from 'prop-types';
import _isEmpty from 'lodash/isEmpty';
import { ConfigurationManager } from 'modules/shell/utils/config';
import { request } from 'modules/shell/utils/request';
import Storage from '../../utils/localforage';

/**
 * Encapsulates configuration data loading an passing it to children components
 * Should be placed at the top of level App components tree and rendered only once
 *
 * @param {String} configUrl
 * @param children
 * @returns {null|*}
 * @constructor
 */
export const ConfigurationLoader = ({ configUrl, children }) => {
  const [config, setConfig] = React.useState(null);
  const [error, setError] = React.useState(null);
  const [configMetaData, setConfigMetaData] = React.useState();

  const fetchConfig = () => {
    // load Config data
    request(configUrl)
      .then(async (response) => {
        // if config is not empty use it for APP initialization
        if (!_isEmpty(response.data)) {
          setConfigMetaData({
            status: response.status,
            statusText: 'Configuration was successfully loaded',
          });
          setConfig(new ConfigurationManager(response.data));

          // config caching
          Storage.APP_CONFIG.setItem('config', response.data);

          return;
        }

        // try to restore previously cached app config and use it if response from S3 is empty
        const { config: appConfigFromCache } = await Storage.APP_CONFIG.getItems();
        if (!_isEmpty(appConfigFromCache)) {
          setConfigMetaData({
            data: response.data,
            status: response.status,
            statusText: 'Configuration was restored from cache',
          });
          setConfig(new ConfigurationManager(appConfigFromCache));

          return;
        }

        // stop app initialization due to absence of Configuration
        const error = new Error('Failed to load Configuration data. response.data is empty no cached config available');
        error.status = 400;

        setConfigMetaData({
          data: response.data,
          status: response.status,
          statusText: 'Configuration data was not loaded or restored from cache. App initialization was terminated.',
          error,
        });

        setError(error);
      })
      .catch((err) => setError(err.response));
  };

  React.useEffect(() => {
    if (!config) {
      fetchConfig();
    }
  }, [config]);

  // config meta data saving
  if (configMetaData) {
    Storage.APP_CONFIG.setItem('configMetadata', configMetaData);
  }

  if (config) {
    return children(config, configMetaData);
  }

  if (error) {
    if (error.status >= 500) {
      return (
        <>
          <h1>Internal Server Error</h1>
          <p>While attempting to load configuration data</p>
        </>
      );
    }

    if (error.status >= 400) {
      return <h1>Failed to load Configuration data</h1>;
    }
  }

  return null;
};

ConfigurationLoader.propTypes = {
  children: PropTypes.func.isRequired,
  configUrl: PropTypes.string,
};

ConfigurationLoader.defaultProps = {
  configUrl: '/static/config.json',
};

export default ConfigurationLoader;
